/**
 * Connector logos, keyed by connector type. One copy: Settings › Connectors draws them large,
 * and the item page draws them small beside each field kept in, or copied to, another system.
 */
import type React from 'react';

// ============================================================================
// Connector Icons
// ============================================================================

export const ConnectorIcons: Record<string, React.ReactNode> = {
  // ── Customer & Market Signal ────────────────────────────────────────────
  intercom: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#1F8EED" />
      <path d="M12 4C7.6 4 4 7.1 4 11c0 2 .9 3.8 2.4 5.1L5.5 19l3.1-1.3C9.5 18.2 10.7 18.4 12 18.4c4.4 0 8-3.1 8-7s-3.6-7.4-8-7.4z" fill="white" fillOpacity="0.95" />
      <rect x="7.5" y="9.5" width="2" height="2" rx="1" fill="#1F8EED" />
      <rect x="11" y="9.5" width="2" height="2" rx="1" fill="#1F8EED" />
      <rect x="14.5" y="9.5" width="2" height="2" rx="1" fill="#1F8EED" />
    </svg>
  ),
  zendesk: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#03363D" />
      <path d="M12 7C12 7 7 7 7 12V17H17V12C17 9.2 14.8 7 12 7Z" fill="#78A300" />
      <path d="M7 17L17 7" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="12" r="2.5" fill="white" />
    </svg>
  ),
  hubspot: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#FF7A59" />
      <circle cx="15" cy="8" r="2.5" fill="white" />
      <circle cx="9" cy="12" r="2.5" fill="white" />
      <circle cx="15" cy="16" r="2.5" fill="white" />
      <path d="M11.3 11L13.2 9.2M11.3 13L13.2 14.8" stroke="white" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  salesforce: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#00A1E0" />
      <path d="M10 8.5C10 7.1 11.1 6 12.5 6C13.5 6 14.3 6.5 14.8 7.3C15.1 7.1 15.5 7 16 7C17.1 7 18 7.9 18 9C18 9.1 18 9.2 17.9 9.3C18.6 9.7 19 10.4 19 11.2C19 12.4 18 13.4 16.8 13.4H8C6.9 13.4 6 12.5 6 11.4C6 10.5 6.6 9.7 7.5 9.5C7.4 9.2 7.3 8.9 7.3 8.5C7.3 7.1 8.4 6 9.8 6C9.9 6 9.9 6 10 6V8.5Z" fill="white" fillOpacity="0.95" />
      <rect x="10" y="13" width="4" height="5" rx="0.5" fill="white" fillOpacity="0.85" />
    </svg>
  ),
  // ── Knowledge & Docs ────────────────────────────────────────────────────
  'google-docs': (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#FFFFFF" stroke="#E5E7EB" />
      <path d="M7 4h7l4 4v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" fill="#4285F4" />
      <path d="M14 4v4h4" fill="none" stroke="#2563EB" strokeWidth="0.75" />
      <rect x="9" y="10" width="6" height="1" rx="0.5" fill="white" />
      <rect x="9" y="12.5" width="6" height="1" rx="0.5" fill="white" />
      <rect x="9" y="15" width="4" height="1" rx="0.5" fill="white" />
    </svg>
  ),
  dropbox: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#0061FF" />
      <path d="M8 5.5L4.5 8 8 10.5 11.5 8 8 5.5zM16 5.5L12.5 8 16 10.5 19.5 8 16 5.5zM4.5 13L8 15.5 11.5 13 8 10.5 4.5 13zM16 10.5L12.5 13 16 15.5 19.5 13 16 10.5zM8 16.2l3.5 2.3 3.5-2.3-3.5-2.2L8 16.2z" fill="white" />
    </svg>
  ),
  sheets: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#FFFFFF" stroke="#E5E7EB" />
      <path d="M7 4h7l4 4v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" fill="#0F9D58" />
      <path d="M14 4v4h4" fill="none" stroke="#0B8043" strokeWidth="0.75" />
      <line x1="9" y1="11" x2="15" y2="11" stroke="white" strokeWidth="0.75" />
      <line x1="9" y1="13.5" x2="15" y2="13.5" stroke="white" strokeWidth="0.75" />
      <line x1="12" y1="9" x2="12" y2="17" stroke="white" strokeWidth="0.75" />
      <rect x="9" y="9" width="6" height="8" rx="0" fill="none" stroke="white" strokeWidth="0.75" />
    </svg>
  ),
  // ── Research & User Insights ─────────────────────────────────────────────
  dovetail: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#FF4F8B" />
      <path d="M12 5L7 10H10V14L12 19L14 14V10H17L12 5Z" fill="white" fillOpacity="0.95" />
    </svg>
  ),
  maze: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#FF5736" />
      <rect x="5" y="5" width="14" height="14" rx="1" fill="none" stroke="white" strokeWidth="1.5" />
      <path d="M5 9H10V13H8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 9H19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 9V19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 13H19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  fullstory: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#1A1A2E" />
      <circle cx="12" cy="12" r="6" stroke="#5B8DEF" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="2.5" fill="#5B8DEF" />
      <path d="M12 6V4M12 20V18M6 12H4M20 12H18" stroke="#5B8DEF" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M9 9L7.5 7.5M16.5 16.5L15 15M15 9L16.5 7.5M7.5 16.5L9 15" stroke="#5B8DEF" strokeWidth="1" strokeLinecap="round" />
    </svg>
  ),
  // ── Data & Engineering ───────────────────────────────────────────────────
  datadog: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#632CA6" />
      <path d="M5 16L8 8L11 14L14 10L17 12L19 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="8" cy="8" r="1.2" fill="#FFCC00" />
      <circle cx="14" cy="10" r="1.2" fill="#FFCC00" />
      <circle cx="19" cy="8" r="1.2" fill="#FFCC00" />
    </svg>
  ),
  snowflake: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#29B5E8" />
      <line x1="12" y1="4" x2="12" y2="20" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="4" y1="12" x2="20" y2="12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="6.3" y1="6.3" x2="17.7" y2="17.7" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="17.7" y1="6.3" x2="6.3" y2="17.7" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="12" cy="12" r="2" fill="white" />
      <circle cx="12" cy="4.5" r="1" fill="white" />
      <circle cx="12" cy="19.5" r="1" fill="white" />
      <circle cx="4.5" cy="12" r="1" fill="white" />
      <circle cx="19.5" cy="12" r="1" fill="white" />
    </svg>
  ),
  // ── AI & Automation (existing) ───────────────────────────────────────────
  perplexity: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#20B8CD" />
      <path d="M12 4L7 8.5V11H9V19H11V13H13V19H15V11H17V8.5L12 4Z" fill="white" />
      <path d="M7 11H17" stroke="white" strokeWidth="1.2" />
      <circle cx="12" cy="8" r="1.5" fill="white" />
    </svg>
  ),
  loom: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#625DF5" />
      <circle cx="12" cy="12" r="5" stroke="white" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="2" fill="white" />
      <line x1="12" y1="4" x2="12" y2="7" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12" y1="17" x2="12" y2="20" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="4" y1="12" x2="7" y2="12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="17" y1="12" x2="20" y2="12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  fireflies: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#7C3AED" />
      <path d="M12 5C12 5 8 8 8 12C8 14.2 9.8 16 12 16C14.2 16 16 14.2 16 12C16 8 12 5 12 5Z" fill="white" fillOpacity="0.9" />
      <path d="M12 16V19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M9.5 17.5L12 19L14.5 17.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="17" cy="7" r="1.2" fill="#FCD34D" />
      <circle cx="7" cy="9" r="0.8" fill="#FCD34D" />
      <circle cx="18" cy="14" r="0.8" fill="#FCD34D" />
    </svg>
  ),
  figma: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#1E1E1E" />
      {/* Figma logo — three circles + two overlapping = F shape */}
      <circle cx="10" cy="7" r="3" fill="#F24E1E" />
      <circle cx="14" cy="7" r="3" fill="#FF7262" />
      <circle cx="10" cy="12" r="3" fill="#A259FF" />
      <circle cx="14" cy="12" r="3" fill="#1ABCFE" />
      <circle cx="10" cy="17" r="3" fill="#0ACF83" />
    </svg>
  ),
  granola: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#16A34A" />
      <rect x="6" y="7" width="12" height="2" rx="1" fill="white" />
      <rect x="6" y="11" width="9" height="2" rx="1" fill="white" />
      <rect x="6" y="15" width="10" height="2" rx="1" fill="white" />
      <circle cx="18" cy="16" r="2.5" fill="#BBF7D0" />
      <path d="M17 16L17.8 16.8L19.2 15.4" stroke="#16A34A" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  n8n: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#EA4B71" />
      <circle cx="6" cy="12" r="2.5" fill="white" />
      <circle cx="12" cy="7" r="2.5" fill="white" />
      <circle cx="18" cy="12" r="2.5" fill="white" />
      <circle cx="12" cy="17" r="2.5" fill="white" />
      <path d="M8 11L10.5 8.5M13.5 8.5L16 11M16 13L13.5 15.5M10.5 15.5L8 13" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  make: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#6D28D9" />
      <circle cx="7" cy="12" r="2" fill="white" />
      <circle cx="17" cy="8" r="2" fill="white" />
      <circle cx="17" cy="16" r="2" fill="white" />
      <path d="M9 12H13.5" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M13.5 10.5L15.2 8.8" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M13.5 13.5L15.2 15.2" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="13.5" cy="12" r="1" fill="white" />
    </svg>
  ),
  raycast: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#FF6363" />
      <path d="M5 5L19 19" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <path d="M5 5L12 5L5 12" fill="white" fillOpacity="0.6" />
      <path d="M19 19L12 19L19 12" fill="white" fillOpacity="0.6" />
      <circle cx="12" cy="12" r="2" fill="white" />
    </svg>
  ),
  mcp: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#0F172A" />
      <rect x="5" y="10" width="14" height="4" rx="1" fill="#334155" />
      <circle cx="8" cy="12" r="1.2" fill="#38BDF8" />
      <circle cx="12" cy="12" r="1.2" fill="#818CF8" />
      <circle cx="16" cy="12" r="1.2" fill="#34D399" />
      <path d="M8 10V7M12 10V5M16 10V7" stroke="#94A3B8" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M8 14V17M12 14V19M16 14V17" stroke="#94A3B8" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  miro: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#FFD02F" />
      <path d="M7 5.5L10 12L7 18.5H9L12 12L9 5.5H7Z" fill="#050038" />
      <path d="M11 5.5L14 12L11 18.5H13L16 12L13 5.5H11Z" fill="#050038" />
      <path d="M15 5.5L18 12L15 18.5H17L20 12L17 5.5H15Z" fill="#050038" />
    </svg>
  ),
  quantive: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#0C2340" />
      {/* Quantive "Q" mark — stylised circular arc with a tail */}
      <circle cx="12" cy="11" r="5" stroke="#00C4CC" strokeWidth="2" fill="none" />
      <path d="M15.5 14.5L18.5 17.5" stroke="#00C4CC" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="11" r="2" fill="#00C4CC" />
    </svg>
  ),
  jira: (
    <svg viewBox="0 0 24 24" className="w-8 h-8">
      <rect width="24" height="24" rx="4" fill="#0052CC" />
      <path
        d="M12.5 4L7 9.5L12 14.5L17 9.5L12.5 4ZM12 10.5L10 8.5L12 6.5L14 8.5L12 10.5Z"
        fill="white"
      />
      <path d="M7 14.5L12 19.5L17 14.5L12 9.5L7 14.5Z" fill="white" fillOpacity="0.7" />
    </svg>
  ),
  slack: (
    <svg viewBox="0 0 24 24" className="w-8 h-8">
      <rect width="24" height="24" rx="4" fill="#4A154B" />
      <path d="M6.5 14.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm0-1h2v-2h-2v2z" fill="#E01E5A" />
      <path d="M9.5 15.5v2a1.5 1.5 0 1 1-3 0v-2h3z" fill="#E01E5A" />
      <path d="M14.5 6.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0zm1 0v2h2v-2h-2z" fill="#36C5F0" />
      <path d="M13.5 9.5h-2a1.5 1.5 0 1 1 0-3h2v3z" fill="#36C5F0" />
      <path d="M17.5 14.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm0-1h-2v-2h2v2z" fill="#2EB67D" />
      <path d="M14.5 15.5v2a1.5 1.5 0 1 1 0-3v1z" fill="#2EB67D" />
      <path d="M9.5 17.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm-1 0v-2h-2v2h2z" fill="#ECB22E" />
      <path d="M10.5 14.5h2a1.5 1.5 0 1 1 0 3h-2v-3z" fill="#ECB22E" />
    </svg>
  ),
  asana: (
    <svg viewBox="0 0 24 24" className="w-8 h-8">
      <rect width="24" height="24" rx="4" fill="#F06A6A" />
      <circle cx="12" cy="7" r="3" fill="white" />
      <circle cx="7" cy="15" r="3" fill="white" />
      <circle cx="17" cy="15" r="3" fill="white" />
    </svg>
  ),
  notion: (
    // Official Notion mark (cube + serif N) — simple-icons path, boxed like peer connectors
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="4" fill="#FFFFFF" stroke="#E5E7EB" />
      <path
        transform="translate(2.5 2.5) scale(0.79)"
        fill="#000000"
        d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.139c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06c-.56-.747-.793-1.306-.793-1.96V2.667c0-.839.374-1.54 1.447-1.632z"
      />
    </svg>
  ),
  linear: (
    <svg viewBox="0 0 24 24" className="w-8 h-8">
      <rect width="24" height="24" rx="4" fill="#5E6AD2" />
      <path d="M5.5 18.5L18.5 5.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <path d="M5.5 12.5L12.5 5.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <path d="M11.5 18.5L18.5 11.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  github: (
    <svg viewBox="0 0 24 24" className="w-8 h-8">
      <rect width="24" height="24" rx="4" fill="#24292F" />
      <path d="M12 5C8.13 5 5 8.13 5 12c0 3.1 2.01 5.73 4.79 6.66.35.06.48-.15.48-.34v-1.2c-1.95.42-2.36-.94-2.36-.94-.32-.81-.78-1.03-.78-1.03-.63-.43.05-.42.05-.42.7.05 1.07.72 1.07.72.62 1.06 1.63.76 2.03.58.06-.45.24-.76.44-.93-1.56-.18-3.2-.78-3.2-3.47 0-.77.28-1.4.72-1.89-.07-.18-.31-.89.07-1.86 0 0 .59-.19 1.93.72a6.7 6.7 0 011.76-.24c.6 0 1.2.08 1.76.24 1.34-.91 1.93-.72 1.93-.72.38.97.14 1.68.07 1.86.45.49.72 1.12.72 1.89 0 2.7-1.64 3.29-3.21 3.46.25.22.48.65.48 1.31v1.94c0 .19.13.41.48.34A7 7 0 0019 12c0-3.87-3.13-7-7-7z" fill="white" />
    </svg>
  ),
  clickup: (
    <svg viewBox="0 0 24 24" className="w-8 h-8">
      <rect width="24" height="24" rx="4" fill="#7B68EE" />
      <path d="M7 13l5 5 5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M7 8l5 5 5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.5" />
    </svg>
  ),
  monday: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#FF3D57" />
      <circle cx="8" cy="9" r="1.6" fill="white" />
      <circle cx="12" cy="9" r="1.6" fill="white" />
      <circle cx="16" cy="9" r="1.6" fill="white" />
      <circle cx="8" cy="13" r="1.6" fill="white" fillOpacity="0.9" />
      <circle cx="12" cy="13" r="1.6" fill="white" fillOpacity="0.9" />
      <circle cx="16" cy="13" r="1.6" fill="white" fillOpacity="0.9" />
      <circle cx="8" cy="17" r="1.6" fill="white" fillOpacity="0.75" />
      <circle cx="12" cy="17" r="1.6" fill="white" fillOpacity="0.75" />
      <circle cx="16" cy="17" r="1.6" fill="white" fillOpacity="0.75" />
    </svg>
  ),
  'adobe-worksheet': (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#ED1C24" />
      <path d="M7 6h10v12H7V6z" fill="white" fillOpacity="0.95" />
      <path d="M9 9h6M9 12h6M9 15h4" stroke="#ED1C24" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M14 6h3l-3 3V6z" fill="#C4122E" />
    </svg>
  ),
  celoxis: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#003D7A" />
      <path d="M6 17V7l4 4 4-4 4 4v10" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="10" cy="13" r="1.4" fill="#5EB3F6" />
      <circle cx="14" cy="11" r="1.4" fill="#5EB3F6" />
    </svg>
  ),
  wrike: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#08CF65" />
      <path d="M7 8h10v2H7V8zm0 4h7v2H7v-2zm0 4h10v2H7v-2z" fill="white" />
      <path d="M16 12l3 2.5V9.5L16 12z" fill="white" fillOpacity="0.85" />
    </svg>
  ),
  productive: (
    <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
      <rect width="24" height="24" rx="4" fill="#0D9488" />
      <rect x="6" y="7" width="12" height="10" rx="1" fill="none" stroke="white" strokeWidth="1.4" />
      <path d="M8.5 11h7M8.5 13.5h5" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="12" cy="16.5" r="1.3" fill="#5EEAD4" />
    </svg>
  ),
  'google-calendar': (
    <svg viewBox="0 0 24 24" className="w-8 h-8">
      <rect width="24" height="24" rx="4" fill="#FFFFFF" stroke="#E5E7EB" />
      <path d="M18 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Z" fill="#4285F4" />
      <path d="M4 9h16" stroke="white" strokeWidth="2" />
      <rect x="6" y="11" width="3" height="3" rx="0.5" fill="white" />
      <rect x="10.5" y="11" width="3" height="3" rx="0.5" fill="white" />
      <rect x="15" y="11" width="3" height="3" rx="0.5" fill="white" />
      <rect x="6" y="15.5" width="3" height="3" rx="0.5" fill="white" />
      <rect x="10.5" y="15.5" width="3" height="3" rx="0.5" fill="white" />
      <path d="M8 3v4M16 3v4" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  zoom: (
    <svg viewBox="0 0 24 24" className="w-8 h-8">
      <rect width="24" height="24" rx="4" fill="#2D8CFF" />
      <path
        d="M4 8.5C4 7.67 4.67 7 5.5 7h8C14.33 7 15 7.67 15 8.5v7c0 .83-.67 1.5-1.5 1.5h-8C4.67 17 4 16.33 4 15.5v-7Z"
        fill="white"
      />
      <path
        d="M15.5 10.2l3.8-2.3c.44-.27 1 .06 1 .57v6.56c0 .51-.56.84-1 .57l-3.8-2.3V10.2Z"
        fill="white"
      />
    </svg>
  ),
  'google-meet': (
    <svg viewBox="0 0 24 24" className="w-8 h-8">
      <rect width="24" height="24" rx="4" fill="#FFFFFF" stroke="#E5E7EB" />
      <path d="M13.5 12l4.5 3.27V8.73L13.5 12z" fill="#00832D" />
      <path d="M6 15.5h7.5V8.5H6a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1z" fill="#0066DA" />
      <path d="M13.5 8.5v3.5l-3-2.6V8.5h3z" fill="#E94235" />
      <path d="M13.5 12l-3 2.6v.9h3V12z" fill="#2684FC" />
      <path d="M6 8.5h4.5l3 2.6V8.5h-7.5z" fill="#00AC47" />
      <path d="M6 15.5h4.5l3-2.6v2.6H6z" fill="#FFBA00" />
    </svg>
  ),
  'microsoft-teams': (
    <svg viewBox="0 0 24 24" className="w-8 h-8">
      <rect width="24" height="24" rx="4" fill="#4B53BC" />
      <circle cx="16" cy="7.5" r="2" fill="#fff" opacity="0.9" />
      <path d="M13 10.5h6.2c.44 0 .8.36.8.8v3.4a3.2 3.2 0 0 1-3.2 3.2A3.8 3.8 0 0 1 13 14.1v-3.6Z" fill="#fff" opacity="0.9" />
      <rect x="4" y="7" width="9" height="10" rx="1.2" fill="#fff" />
      <path d="M6 9.4h5v1.3H9.2V15H7.8v-4.3H6V9.4Z" fill="#4B53BC" />
    </svg>
  ),
};

